import { LocationSummary } from "@/types/place"
import { PublicUser } from "@/types/user"
import {
  CaseReducer,
  createSelector,
  createSlice,
  PayloadAction,
} from "@reduxjs/toolkit"

import { freeze } from "@/lib/utils/collections"
import {
  add,
  allObjectsByKey,
  createSimpleObjectCache,
  SimpleObjectCache,
} from "../simple-object-cache"
import { RootState } from "./redux-store"

const MaxCacheSize = 100
const UserKeyGetter = (user: PublicUser) => user.user_ref
const LocationKeyGetter = (locationSummary: LocationSummary) =>
  locationSummary.entity_ref

export interface PublicUserSliceState {
  publicUserCache: SimpleObjectCache<PublicUser>
  publicUserLocationCache: SimpleObjectCache<LocationSummary>
}

export const publicUserSliceInitialState: PublicUserSliceState = freeze({
  publicUserCache: createSimpleObjectCache(MaxCacheSize),
  publicUserLocationCache: createSimpleObjectCache(MaxCacheSize),
})

const selectSliceState = (state: RootState) => state.publicUserSlice

export const selectPublicUsersByRef = createSelector(
  selectSliceState,
  (state) => allObjectsByKey(state.publicUserCache, UserKeyGetter)
)

export const selectPublicUserLocationsByRef = createSelector(
  selectSliceState,
  (state) => allObjectsByKey(state.publicUserLocationCache, LocationKeyGetter)
)

const addPublicUsers: CaseReducer<
  PublicUserSliceState,
  PayloadAction<PublicUser[]>
> = (state, action) => {
  const publicUsers = action.payload
  add(state.publicUserCache, publicUsers, UserKeyGetter)
}

const addPublicUserLocations: CaseReducer<
  PublicUserSliceState,
  PayloadAction<LocationSummary[]>
> = (state, action) => {
  const locationSummary = action.payload
  add(state.publicUserLocationCache, locationSummary, LocationKeyGetter)
}

const publicUserSlice = createSlice({
  name: "publicUser",
  initialState: publicUserSliceInitialState,
  reducers: {
    addPublicUsers,
    addPublicUserLocations,
  },
})

export type PublicUserSlice = typeof publicUserSlice

export default publicUserSlice
